// The phones' late fallback for the autopilot (spec v2 section 6): if a due time passes and five seconds later no new snapshot
// has arrived, one phone in fifty (by player id) asks the server to run whatever is due. In a room of fifty or fewer every phone
// does, so a small battle never stalls; that is still no more callers than one in fifty of a room of 2,500.
import { useEffect } from 'react';
import type { StateSnapshot } from '@shared/types';
import { CLOSE_AFTER_MS, clueIntervalOf } from '@shared/engine';
import { shardOf } from '@shared/codes';
import { api, serverNow } from '@/lib/api';
import { holdsNewer, useGame } from '@/store/game';

export const LATE_MS = 5000;
export const TICKER_ONE_IN = 50;
export const SMALL_ROOM = 50;
/** How many clues to expect when a snapshot does not say (servers before `cluesTotal` existed): the format's name. */
export const MAX_CLUES = 5;
export const CRITICAL_ATTEMPTS = 6;

export function isTicker(playerId: string, playerCount: number): boolean {
  return playerCount <= SMALL_ROOM || shardOf(playerId, TICKER_ONE_IN) === 0;
}

/** A moment the server should act. `critical` dues (start, unveil, close, next) are retried; a clue is asked for once. */
export interface Due { at: number; critical: boolean }

/**
 * What the server should do next and when, as far as a phone can tell from the snapshot. 30 Oct 2026 (presenter console):
 * the phone's snapshot carries the start and the end of the reveal moved by the stream delay (lib/delay phoneSnapshot);
 * the server acts at its own stamps, so the delay comes off them here. A delayed question shown as its stub (text held
 * until the unveil) backs up the unveil and the close.
 */
export function dueTimes(s: StateSnapshot): Due[] {
  const delay = s.delayMs ?? 0;
  if (s.status === 'lobby') return s.mode === 'autopilot' && !s.hold && typeof s.scheduledStartAt === 'number' ? [{ at: s.scheduledStartAt - delay, critical: true }] : [];
  if (s.status === 'question' && !s.question && s.upcoming) {
    const u = s.upcoming;
    return [{ at: u.startsAt - (s.preRollMs ?? 0), critical: true }, { at: u.deadlineAt + CLOSE_AFTER_MS, critical: true }];
  }
  if (s.status === 'question' && s.question) {
    const q = s.question; const out: Due[] = [];
    if (q.format === 'clues') {
      // Clue k (from 0) falls due at startsAt + k × interval, and none at or after the deadline (as the engine deals them).
      const dealt = Math.max(s.cluesShown ?? 0, q.clues?.length ?? 0);
      const total = typeof q.cluesTotal === 'number' && q.cluesTotal >= 0 ? q.cluesTotal : Math.max(MAX_CLUES, dealt);
      const interval = clueIntervalOf(q);
      for (let k = dealt; k < total; k++) { const at = q.startsAt + k * interval; if (at >= q.deadlineAt) break; out.push({ at, critical: false }); }
    }
    out.push({ at: q.deadlineAt + CLOSE_AFTER_MS, critical: true });
    return out;
  }
  if (s.status === 'reveal') return s.mode === 'autopilot' && !s.hold && typeof s.revealUntil === 'number' ? [{ at: s.revealUntil - delay, critical: true }] : [];
  return [];
}

/** Arms the fallback for the snapshot in hand; any newer snapshot re-arms it. */
export function useLateTick() {
  const version = useGame((s) => s.snapshot?.version);
  const code = useGame((s) => s.snapshot?.code);
  const playerId = useGame((s) => s.session?.playerId);
  useEffect(() => {
    const s = useGame.getState().snapshot;
    if (!s || !playerId) return;
    const dues = dueTimes(s); if (!dues.length) return;
    const timers: number[] = []; let attempts = 0;
    // A newer snapshot held back for the stream delay has arrived too (lib/delay): the server moved on, nothing to back up.
    const unchanged = () => { const cur = useGame.getState().snapshot; return Boolean(cur && cur.code === s.code && cur.version === s.version && !holdsNewer(s.code, s.version)); };
    const fire = (d: Due) => {
      const cur = useGame.getState().snapshot;
      if (!unchanged() || !cur || !isTicker(playerId, cur.playerCount)) return;
      api<{ state?: StateSnapshot }>(`/battles/${s.code}/tick`, { method: 'POST' })
        .then((r) => { if (r?.state) useGame.getState().applySnapshot(r.state); })
        .catch(() => { /* the next attempt or another phone will try */ });
      if (d.critical && ++attempts < CRITICAL_ATTEMPTS) timers.push(window.setTimeout(() => fire(d), LATE_MS));
    };
    for (const d of dues) timers.push(window.setTimeout(() => fire(d), Math.max(0, d.at + LATE_MS - serverNow())));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [version, code, playerId]);
}
